import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { of } from 'rxjs';
import {
    catchError,
    concatMap,
    filter,
    map,
    mergeMap,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

import { Dependencies } from '../../store';
import { pluck } from '../../store.decorator';
import { PayloadAction } from '../../store.model';
import { TableEditorService } from '../../system-configuration/table-editor';

import { rowDtoTransformer } from './row-dto.transformer';
import {
    ActionType,
    CreateRow,
    createRowResult,
    DeleteRow,
    deleteRowResult,
    reloadResult,
    ReloadRows,
    reloadRowsResult,
} from './table-editor.action';
import { TableEditorState } from './table-editor.state';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const reload = (
    action: ActionsObservable<PayloadAction<ActionType, {}>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.TABLE_EDITOR__RELOAD),
        switchMap((act) => {
            const service =
                deps.injector.get<TableEditorService>(TableEditorService);
            return of(null).pipe(
                concatMap(() => service.listTables()),
                map((ret) => {
                    return reloadResult(
                        {
                            ...act.payload,
                            tables: ret,
                        },
                        act.storePath,
                    );
                }),
                catchError(() =>
                    of(reloadResult({ ...act.payload }, act.storePath)),
                ),
                takeUntil(
                    action.pipe(
                        ofType(ActionType.TABLE_EDITOR__RELOAD_CANCEL),
                        filter(
                            (cancelAct) =>
                                cancelAct.storePath === act.storePath,
                        ),
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const reloadRows = (
    action: ActionsObservable<PayloadAction<ActionType, ReloadRows>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.TABLE_EDITOR__RELOAD_ROWS),
        switchMap((act) => {
            const service =
                deps.injector.get<TableEditorService>(TableEditorService);
            return of(null).pipe(
                concatMap(() => service.listRows(act.payload.tableId)),
                map((ret) => {
                    return reloadRowsResult(
                        {
                            ...act.payload,
                            rows: ret,
                        },
                        act.storePath,
                    );
                }),
                catchError(() =>
                    of(reloadRowsResult({ ...act.payload }, act.storePath)),
                ),
                takeUntil(
                    action.pipe(
                        ofType(ActionType.TABLE_EDITOR__RELOAD_ROWS_CANCEL),
                        filter(
                            (cancelAct) =>
                                cancelAct.storePath === act.storePath,
                        ),
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createRow = (
    action: ActionsObservable<PayloadAction<ActionType, CreateRow>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.TABLE_EDITOR__CREATE_ROW),
        switchMap((act) => {
            const rootState = pluck<TableEditorState>(
                state.value,
                act.storePath,
            );
            const service =
                deps.injector.get<TableEditorService>(TableEditorService);
            let success: boolean;
            return of(null).pipe(
                concatMap(() => {
                    return (
                        service
                            .createRow(
                                rootState.table.tableId,
                                rowDtoTransformer(
                                    act.payload.formValue,
                                    rootState.table.columns,
                                ),
                            )
                            // eslint-disable-next-line no-return-assign
                            .pipe(tap((ret) => (success = ret != null)))
                    ); // no location header is returned
                }),
                concatMap(() => service.listRows(rootState.table.tableId)),
                map((ret) => {
                    return createRowResult(
                        {
                            ...act.payload,
                            success,
                            rows: ret,
                        },
                        act.storePath,
                    );
                }),
                catchError(() =>
                    of(createRowResult({ ...act.payload }, act.storePath)),
                ),
                takeUntil(
                    action.pipe(
                        ofType(ActionType.TABLE_EDITOR__CREATE_ROW_CANCEL),
                        filter(
                            (cancelAct) =>
                                cancelAct.storePath === act.storePath,
                        ),
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const deleteRow = (
    action: ActionsObservable<PayloadAction<ActionType, DeleteRow>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.TABLE_EDITOR__DELETE_ROW),
        mergeMap((act) => {
            const rootState = pluck<TableEditorState>(
                state.value,
                act.storePath,
            );
            const service =
                deps.injector.get<TableEditorService>(TableEditorService);
            return of(null).pipe(
                concatMap(() =>
                    service.deleteRow(rootState.table.tableId, act.payload.row),
                ),
                map((ret) => {
                    return deleteRowResult(
                        {
                            ...act.payload,
                            success: ret,
                        },
                        act.storePath,
                    );
                }),
                catchError(() =>
                    of(deleteRowResult({ ...act.payload }, act.storePath)),
                ),
                takeUntil(
                    action.pipe(
                        ofType(ActionType.TABLE_EDITOR__DELETE_ROW_CANCEL),
                        filter(
                            (cancelAct) =>
                                cancelAct.storePath === act.storePath,
                        ),
                    ),
                ),
            );
        }),
    );

export const adminTableEditorEpics = [reload, reloadRows, createRow, deleteRow];
